import React, { useState, useEffect } from "react";
import { Modal, Input, Button, Table, Tooltip, AutoComplete, notification } from "antd";
import ApiService from "../../utils/ApiService";
import useUserStore from "../../stores/store";
import CustomLoader from "../menu/CustomLoader";
import { constants } from "../../constant/constants";
import CustomInput from "../CustomInput";
import CustomInputNumber from "../CustomInputNumber";

const FoodResetModal = ({ openModal, records }) => {
  const [api, contextHolder] = notification.useNotification();
  const { onFinish, onCancel } = records;
  const { user, restaurantId, networkCall, setNetworkCall } = useUserStore();
  const [stockValues, setStockValues] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [changedStockValues, setChangedStockValues] = useState([]);
  const [filteredValues, setFilteredValues] = useState([]);

  const [loading, setLoading] = useState(false);

  const openNotification = (type, message, description, placement = "topRight") => {
    api[type]({
      message,
      description,
      placement,
    });
  };

  const fetchAllFoodItems = async () => {
    setNetworkCall(true);
    try {
      const response = await ApiService.GET_FOOD_STOCK_LIST(
        { restaurantId: restaurantId.toString() },
        user.token.token
      );
      setNetworkCall(false);

      if (response.code === 200) {
        const foods = response.payload.records.map((food) => ({
          foodId: food.foodId,
          name: food.name,
          id: food.foodId,
          availableQuantity: food.availableQuantity || 0,
          defaultQuantity: food.defaultQuantity || 0,
        }));
        setStockValues(foods);
        setFilteredValues(foods); // Initialize filtered values with all foods
      } else {
        console.error("Failed to fetch food items:", response.message);
      }
    } catch (error) {
      setNetworkCall(false);
      console.error("Error fetching food items:", error);
    }
  };

  useEffect(() => {
    // Update filtered values whenever stockValues or searchText changes
    const filtered = stockValues.filter((item) => item.name.toLowerCase().includes(searchText.toLowerCase()));
    setFilteredValues(filtered);
  }, [stockValues, searchText]);

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleStockChange = (value, foodId, field) => {
    const intValue = parseInt(value, 10);

    if (intValue < 0) {
      return 0;
    }

    const updatedValues = stockValues.map((item) => {
      if (item.id === foodId) {
        return { ...item, [field]: isNaN(intValue) ? 0 : intValue };
      }
      return item;
    });

    setStockValues(updatedValues);

    const isAlreadyChanged = changedStockValues.find((item) => item.id === foodId);

    const updatedChangedValues = isAlreadyChanged
      ? changedStockValues.map((item) =>
          item.id === foodId ? { ...item, [field]: isNaN(intValue) ? 0 : intValue } : item
        )
      : [...changedStockValues, { id: foodId, [field]: isNaN(intValue) ? 0 : intValue }];

    setChangedStockValues(updatedChangedValues);
  };

  const handleFieldChange = (nameValObj) => {
    const [id, field] = nameValObj.name.split("_");
    handleStockChange(nameValObj.value, id, field);
  };

  const handleUpdate = async () => {
    const updatedFoodList = stockValues.map(({ id, name, ...rest }) => rest);
    const payload = {
      restaurantId: restaurantId,
      newStocks: updatedFoodList,
    };
    try {
      const res = await ApiService.PATCH_FOOD_STOCK_LIST(payload, user.token.token);
      if (res.code === 200) {
        openNotification(constants.NOTIFICATION.SUCCESS, "Success", res.message);
        // Reset search and fetch updated data
        setSearchText("");
        await fetchAllFoodItems();
      } else {
        openNotification(constants.NOTIFICATION.ERROR, "Error", res.message);
      }
    } catch (error) {
      openNotification(constants.NOTIFICATION.ERROR, "Error", error);
    } finally {
      onFinish();
    }
  };

  useEffect(() => {
    fetchAllFoodItems();
  }, [openModal]);

  const columns = [
    {
      title: "Food Name",
      dataIndex: "name",
      key: "name",
      width: "60%",
    },
    {
      title: "Current Stock",
      dataIndex: "availableQuantity",
      key: "availableQuantity",
      width: "25%",
      render: (text, record) => (
        <CustomInputNumber
          name={`${record.id}_availableQuantity`}
          value={record.availableQuantity}
          setValue={handleFieldChange}
          max={999999}
          isDecimalAllowed={false}
          shouldSetValueSetObj={true}
        />
      ),
    },
    {
      title: "Default Stock",
      dataIndex: "defaultQuantity",
      key: "defaultQuantity",
      width: "25%",
      render: (text, record) => (
        <CustomInputNumber
          name={`${record.id}_defaultQuantity`}
          value={record.defaultQuantity}
          setValue={handleFieldChange}
          max={999999}
          isDecimalAllowed={false}
          shouldSetValueSetObj={true}
        />
      ),
    },
  ];

  return (
    <Modal
      title="Stock Update"
      maskClosable={false}
      open={openModal}
      onOk={handleUpdate}
      onCancel={onCancel}
      centered
      width="70%"
      styles={{
        body: {
          height: "70vh", // 80% of viewport height
          overflowY: "auto", // enable scroll inside body
        },
      }}
      footer={[
        <Tooltip
          key={"reset"}
          title="Reset current stock to initial stock. It will just replace the current stock with initial stock. To save them click on Update button"
        >
          <Button
            style={{
              backgroundColor: "#572D86",
              color: "#FFFFFF",
              fontFamily: "ubold",
            }}
            key="reset"
            onClick={() =>
              setStockValues(
                stockValues.map((item) => ({
                  ...item,
                  availableQuantity: item.defaultQuantity,
                }))
              )
            }
          >
            Reset
          </Button>
        </Tooltip>,
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleUpdate}
          style={{
            backgroundColor: "#FFD700",
            color: "#000000",
            fontFamily: "ubold",
          }}
        >
          Update
        </Button>,
      ]}
    >
      {networkCall ? (
        <CustomLoader />
      ) : (
        <>
          {contextHolder}
          <AutoComplete
            style={{ width: "100%", marginBottom: "16px" }}
            onSearch={handleSearch}
            placeholder="Search food items"
            value={searchText}
            allowClear
          />
          <Table
            rowKey="id"
            dataSource={filteredValues}
            columns={columns}
            // scroll={{ y: 300 }}
            pagination={false}
          />
        </>
      )}
    </Modal>
  );
};

export default FoodResetModal;
